import { AlertTriangle, ArrowRight, CircleAlert } from 'lucide-preact'
import { useCrmConnectionAlert } from '@/hooks/useCrmConnectionAlert'
import { IntegrationConnectionStatus } from '@/types/integrations'
import { routes } from '@/utils/routes'
import { crmLabel } from '../crmSlot'

interface CrmConnectionAlertBannerProps {
    clientCompanyId: string | undefined
    /** The console wears its own tokens (`cx-*`), not the app's Tailwind palette. */
    variant?: 'app' | 'console'
    /** Wraps the banner, so layout spacing appears only when there is a banner to space. */
    containerClassName?: string
}

/**
 * Tells a company admin, on the campaign screens, that their CRM connection is broken and where to
 * fix it (ORB-966). Renders nothing for anyone else, or while the CRM is healthy. The same break is
 * also emailed to them once; this is the reminder for as long as it lasts.
 */
export function CrmConnectionAlertBanner({ clientCompanyId, variant = 'app', containerClassName }: CrmConnectionAlertBannerProps) {
    const brokenCrm = useCrmConnectionAlert(clientCompanyId)
    if (!brokenCrm || !clientCompanyId) return null

    const crm = crmLabel(brokenCrm.Provider as string)
    const message = brokenCrm.Status === IntegrationConnectionStatus.AuthExpired
        ? `The ${crm} login has expired, so nothing is reaching ${crm}. Reconnect it to resume syncing.`
        // Reconnect, not reset: a CRM panel offers only Connect once the connection is not Active.
        : `Syncing to ${crm} stopped after repeated failures, so nothing is reaching ${crm}. Reconnect it to resume syncing.`
    const href = routes.settingsIntegrations(clientCompanyId)

    const banner = variant === 'console'
        // `.cx-admin-notice` is a flex row, so the message and its link share one span.
        ? (
            <p className="cx-admin-notice bad" role="alert">
                <CircleAlert size={15} aria-hidden="true" />
                <span>{message} <a href={href}><b>Open integrations</b></a></span>
            </p>
        )
        : <AppBanner message={message} href={href} />

    return containerClassName ? <div className={containerClassName}>{banner}</div> : banner
}

function AppBanner({ message, href }: { message: string; href: string }) {
    return (
        <div
            role="alert"
            className="flex flex-wrap items-start gap-x-3 gap-y-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
            <span className="flex-1 min-w-[12rem]">{message}</span>
            <a
                href={href}
                className="inline-flex items-center gap-1 rounded font-medium underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            >
                Open integrations
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
        </div>
    )
}
